/**
 * Motion, in one place: whether the game may move, and how long and how
 * hard it moves when it may.
 *
 * "May it move" has two voices — the OS setting (`prefers-reduced-motion`)
 * and the in-game toggle (`state/settings`) — and either one is enough.
 * Everything that animates asks `reducedMotion()`; nothing else reads the
 * media query or the setting (design/quick-specs/life-layer.md §5). The OS
 * answer is held in a variable kept fresh by a `change` listener, so asking
 * is a boolean read, safe from a frame loop; it is never a CSSOM query per
 * frame. The in-game toggle also mirrors onto `<html data-reduce-motion>`,
 * which is how the global CSS override in `style.css` hears it.
 *
 * `motionScale()` returns zero rather than a small number when reduced motion
 * is set: everything whose only job is to move is multiplied by it and so
 * disappears, instead of every call site growing a branch. `effectDetail()`
 * is the other axis — a coarse pointer is a phone, and a phone gets fewer
 * quads per event rather than a different-looking event.
 *
 * The easing and duration tokens mirror the CSS custom properties of the
 * same names in `style.css` (`motion.test.ts` pins that they agree), for the
 * code that animates from script (Web Animations, gsap) rather than from a
 * stylesheet.
 */
import { watch } from 'vue';

import { reduceMotion } from '@/state/settings';

/** `--ease`: the one exponential curve every CSS transition uses. */
export const EASE = 'cubic-bezier(0.16, 1, 0.3, 1)';
/** `--dur`: a control answering the hand. */
export const DUR_MS = 180;
/** `--dur-sheet`: a panel or sheet arriving (life-layer.md §3, P0). */
export const DUR_SHEET_MS = 320;
/** `--pop`: overshoot for things that land, like the tarp's tag (life-layer.md §2). */
export const POP = 'cubic-bezier(0.2, 1.6, 0.4, 1)';

const REDUCE_QUERY = '(prefers-reduced-motion: reduce)';

let osReduced: boolean | null = null;
let osQuery: MediaQueryList | null = null;

function onOsChange(e: MediaQueryListEvent): void {
  osReduced = e.matches;
  syncDocument();
}

function readOs(): boolean {
  if (osReduced === null) {
    osQuery = typeof matchMedia === 'function' ? matchMedia(REDUCE_QUERY) : null;
    osReduced = osQuery?.matches ?? false;
    osQuery?.addEventListener?.('change', onOsChange);
  }
  return osReduced;
}

/** The OS asks for stillness, or the player did in the settings. */
export function reducedMotion(): boolean {
  return readOs() || reduceMotion.value;
}

/** `ms`, or 0 when the game must hold still: the one way a duration is chosen. */
export function dur(ms: number): number {
  return reducedMotion() ? 0 : ms;
}

function syncDocument(): void {
  if (typeof document === 'undefined') return;
  document.documentElement.toggleAttribute('data-reduce-motion', reducedMotion());
}

// Module scope on purpose: the attribute must follow the toggle from the
// moment anything imports this, not from a call someone must remember.
watch(reduceMotion, syncDocument, { immediate: true });

export function motionScale(): number {
  return reducedMotion() ? 0 : 1;
}

/**
 * Multiplier on the number of particles an event is worth, 0…1. Effects read
 * it once at construction and round their own counts with it; nothing here is
 * consulted per frame.
 */
export function effectDetail(coarse: boolean): number {
  return (coarse ? 0.6 : 1) * (motionScale() > 0 ? 1 : 0.6);
}

/** Test seam: forget the cached OS reading. */
export function resetMotionPreference(): void {
  osQuery?.removeEventListener?.('change', onOsChange);
  osQuery = null;
  osReduced = null;
  if (typeof document !== 'undefined') document.documentElement.removeAttribute('data-reduce-motion');
}
